/**
 * Rail geometry (design 175–340 left, 1152–1605 right).
 *
 * ## The clamp rule, and why it is not a constant
 *
 * The plan asks for a 300–620 px rail. Taken as a constant that is wrong at the
 * viewport this project has committed to supporting: **1280 × 720** (shell.md
 * §Layout). Two rails at 620 leave 40 px of centre pane at 1280 — a console
 * whose workspace has vanished but whose furniture is intact.
 *
 * So the ceiling is computed, and the rule is:
 *
 * > **The centre pane keeps `CENTRE_MIN_WIDTH`.** A rail may be dragged to any
 * > width in `[RAIL_MIN, RAIL_MAX]` that leaves it, given whatever the *other*
 * > rail is currently taking.
 *
 * At 1280 with both rails open that ceiling is 340 each; collapse one and the
 * other may take its full 620. At 1920 both reach 620 and the rule never bites.
 *
 * The clamp is applied at **render**, not only at drag: a rail dragged wide
 * while its neighbour was collapsed must give the width back when the neighbour
 * reopens, or reopening it would push the centre pane under the floor. The
 * *stored* width is left alone — clamping is a fact about the window, and a
 * console dragged narrow and back must not have forgotten how wide its rails
 * were.
 *
 * ## Why the layout width is floored at the minimum viewport
 *
 * `ConsoleShell` sets `min-width: 1280` and a narrower window **scrolls** rather
 * than reflowing (Phase 1, answer 2). So the widths are computed against
 * `max(viewportWidth, 1280)` — which is what makes the rule total: at the
 * narrowest layout the console ever draws, two floors plus the centre floor
 * (300 + 300 + 640 = 1240) still fit inside 1280.
 */

import { CONSOLE_MIN_VIEWPORT } from '../viewport.js';

export const RAIL_MIN_WIDTH = 300;
export const RAIL_MAX_WIDTH = 620;
/** The collapsed rail — a 30 px stub with a vertical label. */
export const RAIL_STUB_WIDTH = 30;

/**
 * The 1 px rules the design draws between panels (design 164), two of them.
 *
 * Counted, because a floor that ignores them is not the floor it claims: at 1280
 * the centre pane measured 638 rather than 640 until this was subtracted, and a
 * rule that is off by the width of its own separators is a rule nobody will
 * trust the next time it is inconvenient.
 */
export const PANEL_RULES_WIDTH = 2;

/**
 * The floor the centre pane keeps.
 *
 * Measured against the same constraint as the height floor: at 1280 with both
 * rails at their 300 px floor the centre pane is 680, so 640 is reachable at the
 * stated minimum with 40 px of slack for one rail to grow. Every map, the MW
 * timeline dock and the recognition table are drawn into it.
 */
export const CENTRE_MIN_WIDTH = 640;

export type RailSide = 'left' | 'right';

export interface RailLayout {
  readonly left: number;
  readonly right: number;
  /** What a drag on that rail may reach right now. Also what the resize handle clamps to. */
  readonly leftMax: number;
  readonly rightMax: number;
  /**
   * What is left for the centre pane.
   *
   * Computed rather than measured, because the things that need it — the
   * recognition table's column priorities (Phase 4) and every map's viewport —
   * need it on the render that lays them out, and a `ResizeObserver` answers one
   * frame later. It is exact: the layout is three widths and two 1 px rules.
   */
  readonly centre: number;
}

/**
 * The default width for a rail, before the operator has dragged one.
 *
 * The design widens both rails on a wide screen and keeps them narrow otherwise
 * (`w >= 1560 ? 396 : 344` for the right rail). Same breakpoint here.
 */
export function defaultRailWidth(side: RailSide, viewportWidth: number): number {
  const wide = viewportWidth >= 1560;
  return side === 'right' ? (wide ? 396 : 344) : wide ? 340 : 300;
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

/**
 * Both rails' rendered widths, and the ceiling each may be dragged to.
 *
 * **The right rail is resolved first and the left takes what is left.** The
 * order has to be fixed — a symmetric rule where each rail clamps against the
 * other's clamped width does not converge — and this is the right way round: at
 * 1280 with both rails open the surplus over the two floors is 40 px, and it
 * goes to the rail holding the selection panel, which is the one the operator is
 * reading when the window is that small. It is also the rail the design gives
 * the drag handle to.
 */
export function railLayout(args: {
  readonly viewportWidth: number;
  readonly leftOpen: boolean;
  readonly rightOpen: boolean;
  readonly leftWidth: number;
  readonly rightWidth: number;
}): RailLayout {
  const available = Math.max(args.viewportWidth, CONSOLE_MIN_VIEWPORT.width) - PANEL_RULES_WIDTH;

  const leftFloor = args.leftOpen ? RAIL_MIN_WIDTH : RAIL_STUB_WIDTH;
  const rightMax = clamp(available - CENTRE_MIN_WIDTH - leftFloor, RAIL_MIN_WIDTH, RAIL_MAX_WIDTH);
  const right = args.rightOpen ? clamp(args.rightWidth, RAIL_MIN_WIDTH, rightMax) : RAIL_STUB_WIDTH;

  const leftMax = clamp(available - CENTRE_MIN_WIDTH - right, RAIL_MIN_WIDTH, RAIL_MAX_WIDTH);
  const left = args.leftOpen ? clamp(args.leftWidth, RAIL_MIN_WIDTH, leftMax) : RAIL_STUB_WIDTH;

  return { left, right, leftMax, rightMax, centre: available - left - right };
}
